<template>
  <div id="transcript">
    <div ref="fill-header" class="px-2">
      <v-layout row>
        <v-flex xs12>
          <v-text-field
            class="transcript-search"
            name="caption-search"
            label="Search"
            clearable
            prepend-icon="search"
            v-model="searchText"
            ref="search"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </div>
    <div class="transcript-wrapper">
      <v-list v-if="!captionsFailed">
        <div id="instructor-transcript-text" class='transcript-subheader' v-if='!bulbHasCaptions'>
          Live Transcript is not available for this {{termForBulb}}.
        </div>
        <div class='transcript-subheader' v-if='bulbHasCaptions && filteredTranscript.length === 0'>
          {{ transcriptDescriptionText }}
        </div>
        <template v-for="(caption, index) in filteredTranscript">
          <div grid-list-md class="ppcaption" :key="index" :id="'caption-' + index" :class="{'active-caption': index === activeIndex}">
            <v-layout row>
              <v-flex xs12 class="right">
                <div class="caption-content mb-3" v-html="caption.text" @click="jump(caption)" @keyup.enter="jump(caption)" tabindex="0" aria-label='jump to caption time'></div>
              </v-flex>
            </v-layout>
          </div>
        </template>
      </v-list>
      <div v-else>
        <v-alert color="warning" icon="priority_high" value="true">
          Unable to parse captions.
        </v-alert>
      </div>
    </div>
    <div class="loading-screen" v-if="loadingTranscript">
      <v-progress-circular indeterminate v-bind:size="50" color="primary"></v-progress-circular>
    </div>
  </div>
</template>

<script>
/* global _ */
/* global dataLayer */
import _ from 'lodash';
var vtt = require('@/js/vtt/vtt.js');
import IScroll from 'iscroll/build/iscroll-probe.js';
import EventBus from '@/buses/EventBus';
import PlayerService from '@/services/PlayerService';
import { timestampToSeconds } from '@/js/helpers';
import { mapGetters, mapMutations, mapState } from 'vuex';

window.IScroll = IScroll
export default {
  name: 'transcript',
  props: [ ],
  components: { },
  data () {
    return {
      captionsFailed: false,
      loadingTranscript: false,
      parsedTranscripts: { }, // object of parsed captions files
      previousAutoScrollTime: null,
      scrollbar: null,
      searchText: '',
    }
  },
  computed: {
    ...mapState('player', [
      'formatReferer',
      'getDisplayTime',
      'intersectingCrop',
      'isLiveSession',
      'sidebarTab',
      'sidebarOpen',
      'showInteractionsTab',
    ]),
    ...mapGetters('player', [
      'bulb',
      'bulbInitialized',
      'displayTime',
      'maxTime',
      'termForBulb',
    ]),
    allCaptions() {
      // get an array of all caption objects from all bulbVideos
      return  _.flatMap(this.bulb.bulbVideos, 'bulbVideoCaptions');
    },
    activeCaptions() {
      if (!this.bulbInitialized) return [];
      // each bulb video tracks which caption set is active in bulbVideo.captionId property
      const activeCaptionIds = _.flatMap(this.bulb.bulbVideos, bv => String(bv.captionId)) || [];
      // return an array of only the active caption objects
      return _.filter(this.allCaptions, (c) => activeCaptionIds.includes(String(c.id)))
    },
    activeIndex() {
      return _.findIndex(this.filteredTranscript, (t) =>  t.start < this.displayTime && this.displayTime < t.end)
    },
    bulbHasCaptions() {
      return this.allCaptions.length > 0;
    },
    filteredTranscript() {
      if (!this.loadingTranscript) {
        let transcript = []
        this.activeCaptions.forEach((c, i) => {
          if (
            c.id !== '-1'
            && c.loaded
            && c.type !== 'youtube'
            && c.type !== 'vimeoEmbed'
          ) {
            transcript = transcript.concat(this.parsedTranscripts[c.id]);
          }
        });
        transcript = _.sortBy(transcript, 'start')
        if (this.searchText) {
          return transcript.filter((caption) => {
            let regex = new RegExp('(' + this.searchText + ')', 'i');
            return caption.text.match(regex) && caption.start <= this.maxTime;
          });
        } else if (transcript.length > 0) {
          return  _.filter(transcript, c => c.start <= this.maxTime);
        }
        return transcript;
      }
      return [];
    },
    isYouTubeActiveStream() {
      const currentStreamIndex = _.findIndex(this.$parent.$parent.$refs.videoInstance, { currentSegment: true });
      if (currentStreamIndex <= -1) return false;
      return this.$parent.$parent.$refs.videoInstance[currentStreamIndex]?.activeStream?.type === 'video/youtube';
    },
    transcriptDescriptionText() {
      return this.isYouTubeActiveStream ? 'Live Transcript is not supported for captions streamed from YouTube' : 'Enable captions to view a Live Transcript';
    },
    visible() {
      return this.sidebarTab === 'transcript-tab' && this.sidebarOpen && !this.showInteractionsTab;
    },
  },
  methods: {
    ...mapMutations('player', [
      'setSnackbar',
    ]),
    cueInCropSegment(cueTimes, videoSkips) {
      /**
       * Helper method
       * Given a cue, check if start and end times exist within a cropped out segment
       * cueTimes: object of start and end times in seconds {start: 13, end: 16}
       * bulb: the bulb the cueTimes belong to
       */
      return this.intersectingCrop(cueTimes.start, videoSkips) && this.intersectingCrop(cueTimes.end, videoSkips);
    },
    jump(caption) {
      if (this.isLiveSession) {
        this.setSnackbar({
          color: 'warning',
          text: `Jumping is disabled for session ${this.termForBulb}s.`,
          timeout: 5000,
        });
        return;
      }
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: caption.start,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
    },
    async loadTranscriptFile(caption) {
      /**
       * Takes a caption object
       * if the caption file does not exist in loadedTranscripts:
       *    make ajax call to download the vtt file for the transcript component
       *    add strigified version to the loadedTranscripts and pass to the transcript question component
       */
      if (caption.error) {
        this.setSnackbar({
          color: 'warning',
          text: 'A portion of the transcript could not be loaded.',
          timeout: 5000,
        });
        return;
      }
      this.loadingTranscript = true;
      const ccType = (() => {
        // Helper method to generate the caption type to make sure we use the correct authentication when running captions through the proxy
        // techconnect has an integration with youtube and so if they send us a youtube video we need to pass it as youtube
        if (caption.type === 'techconnect' && caption.ccUrl.includes('youtube')) return 'youtube';
        // otherwise just use the default techconnect type
        return caption.type;
      })()

      PlayerService.getCaptions({ captionUrl: caption.ccUrl, type: ccType, teacherId: this.bulb.teachers[0].id})
      .then(captionFile => {
        this.loadingTranscript = false;
        caption.loaded = true;
        caption.text = captionFile.data; // attach file contents to caption
        this.parseVtt(caption);
      })
      .catch(err => {
        dataLayer.errorLog(err);
        caption.error = true;
        this.loadingTranscript = false;
        this.setSnackbar({
          color: 'error',
          text:
            _.has(err, 'response.data.message')
            && !err.response.data.message.includes('vimeo') ? err.response.data.message : `Error retrieving transcript file. Please try a different set.`,
          timeout: 5000,
	      });
      });
    },
    parseVtt(captionSet) {
      /**
       * Given a vtt file, parse it into an array of captions as an object with the text, start, end and whether or not it's the current transcript
       *
       */
      var WebVTT = vtt.WebVTT;
      var parser = new WebVTT.Parser(window, vtt.WebVTT.StringDecoder());
      // get video skip segments
      const videoSkips = _.find(this.bulb.bulbVideos,{id:captionSet.bulbVideoId}).allCrops;
      // setup array key pair for parsedTranscripts
      if (!this.parsedTranscripts.capitonId) this.parsedTranscripts[captionSet.id] = []
        this.captionsFailed = false;
        try {
          parser.oncue = (cue) => {
            if (!this.cueInCropSegment({ start: cue.startTime, end: cue.endTime}, videoSkips)) {
              this.parsedTranscripts[captionSet.id].push({
                text: cue.text.replace(/WEBVTT FILE/g, '').replace(/<\/?[^>]+(>|$)/g, ""), // remove karaoke timestamps
                start: this.getDisplayTime(captionSet.bulbVideoId, cue.startTime),
                end: this.getDisplayTime(captionSet.bulbVideoId, cue.endTime),
                current: false,
              });
            }
          };
          parser.onflush = () => {
            this.loadingTranscript = false;
            this.$set(captionSet, 'vjsLoad', true);
          }
          parser.parse(captionSet.text);
          parser.flush()
        } catch(e) {
          console.log('transcript parsing error', e)
          dataLayer.errorLog(e, this.$options.name);
          this.loadingTranscript = false;
          this.captionsFailed = true;
        }
    },
    scrollToCaptionByIndex(captionIndex) {
      const captionElement = this.$el.querySelector(`#caption-${captionIndex}`);
      if (
        !captionElement
        || !this.scrollbar
      ) return;
      this.scrollbar.scrollToElement(captionElement, 0.25, true, true);
    },
  },
  watch: {
    'activeCaptions': {
      handler() {
        this.activeCaptions.forEach((c, i) => {
          if (
            c.type === 'youtube'
            || c.type === 'vimeoEmbed'
          ) {
            c.loaded = true;
            c.vjsLoad = true;
            return;
          }
          if (
            !this.parsedTranscripts[c.id]
            && c.id !== '-1'
            && !c.loaded
          ) {
            this.loadTranscriptFile(c);
          }
        })
      },
      deep: true
    },
    'filteredTranscript': {
      handler() {
        this.$nextTick(() => { // was loading but now is finished
          this.scrollbar.refresh(); // refresh to update hight and scrolling
        })
      },
      deep: true,
    },
    'sidebarTab': {
      handler() {
        if (this.sidebarTab === 'transcript-tab') {
          this.$nextTick(() => { // was loading but now is finished
            this.scrollbar.refresh();
          })
        }
      }
    },
    'visible'(){
      if (this.visible) {
        this.$refs.search.focus();
      }
    },
    activeIndex(newValue) {
      this.scrollToCaptionByIndex(newValue)
    },
  },
  async mounted() {
    await this.$nextTick();
    this.scrollbar = new IScroll('.transcript-wrapper', {
      scrollbars: true,
      mouseWheel: true,
      interactiveScrollbars: true,
      shrinkScrollbars: 'clip',
      fadeScrollbars: true,
      click: true,
      bounce: false,
      disablePointer: true,
      disableTouch: false,
      disableMouse: false,
      probeType: 2,
    });
  }
};
</script>

<style>
  #transcript{
    display:flex;
    flex-direction: column;
    min-height: 0;
    flex-grow: 1;
    overflow: hidden;
    background: #fff;
    border-radius:2px;
  }

  .transcript-wrapper {
    height: 100%;
    position:relative;
    overflow: hidden;
  }

  .transcript-subheader{
    text-align: center;
  }
  .discussion-form {
    min-height: 100px;
    display: flex;
    max-width:100%;
    align-items: flex-start;
    padding-top: 5px;
  }

  .discussion-form .form-left {
    width: 80%;
  }

  .input-group {
    padding: 18px 0 0;
  }
  .input-group label {
    height: 30px;
  }

  .v-input-group__details {
    display: block;
  }

  .captions-wrapper .more-messages-below {
    position: absolute;
    bottom: 0;
    width: 100%;
    text-align: center;
    padding: 0.5rem 0;
    background: rgba(0,0,0,0.6);
    color: white;
    user-select: none;
    cursor: pointer;
  }

  .loading-screen {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-color: rgba(255,255,255,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ppcaption {
    margin: 0 2.5%;
    padding: 0 10px;
  }

  .ppcaption .caption-content {
    word-wrap: break-word;
    white-space: normal;
  }

  .ppcaption .caption-content:hover {
    text-decoration: underline;
    cursor: pointer;
  }

  .comment-actions a:not(:last-of-type) {
    margin-right: 8px;
  }

  .comment-actions .v-btn .v-btn__content {
    padding: 0;
    transition: none;
  }

  .ppcaption {
    border-left: 2px solid transparent;
  }

  .ppcaption img {
    max-width: 100%;
  }

  .ppcaption a:hover {
    color: rgba(17,17,17,0.8) !important;
    font-weight: 700;
  }

</style>
